import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AuthLayout from '../../components/layout/AuthLayout';
import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import { useMemberApi } from '../../hooks/useMemberApi';
import { getCertificateTypeOptions, getCertificateComponentId } from '../../constants/CertificateTypes';
import { isValidGuid } from '../../utils/validateGuidUtil';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { LookupMemberByCertificateDTO } from '../../services/apiClient';

const VerifyCertificateForm: React.FC = () => {
  const [formData, setFormData] = useState<LookupMemberByCertificateDTO>({});
  const [certificateType, setCertificateType] = useState<string>(''); 
  const [apiError, setApiError] = useState<string | null>(null);

  const { lookupMemberByCertificate, isLoading } = useMemberApi();
  const navigate = useNavigate();

  const certificateOptions = getCertificateTypeOptions();

  const validationRules: ValidationRules = {
    componentId: [{ required: true, message: 'Please select a certificate type' }],
    certificateCode: [
      { required: true, message: 'Certificate code is required' },
      {
        custom: (value) => !value || isValidGuid(value),
        message: 'Certificate code must be a valid GUID format',
      },
    ],
  };

  const { errors, validate, clearError } = useFormValidation<LookupMemberByCertificateDTO>(validationRules);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setApiError(null);

    if (!validate(formData)) return;

    try {

      const response = await lookupMemberByCertificate(formData.componentId ?? '', formData.certificateCode ?? '');

      if (response && response.data) {
        navigate(`/member/verify/${response.data}/${formData.componentId ?? ''}/${formData.certificateCode}`);
      } else {
        setApiError('Certificate not found. Please check your details and try again.');
      }
    } catch (error: any) {
      console.error('Certificate lookup error:', error);
      setApiError(error?.message || 'An error occurred while verifying the certificate. Please try again later.');
    }
  };

  const handleComponentIdChange = (type: string) => {
    const componentId = getCertificateComponentId(type);
    setCertificateType(type);
    setFormData((prev) => ({...prev, componentId}));
  }

  return (
    <AuthLayout
      title="Verify Certificate"
      description="Enter your certificate details to verify its authenticity."
    >
      <div className="mt-8">
        {apiError && (
          <div className="mb-4 text-sm text-red-600">{apiError}</div>
        )}
        <form onSubmit={handleSubmit}>
          <FormField
            id="certificateType"
            label="Certificate Type"
            type="dropdown"
            value={certificateType}
            onChange={(e) => {
              handleComponentIdChange(e.target.value);
              clearError('certificateType');
            }}
            placeholder="Select certificate type"
            options={certificateOptions.map((option) => ({
              value: option.display,
              label: option.display,
            }))}
            error={errors.componentId}
          />

          <FormField
            id="certificateCode"
            label="Certificate Code"
            type="text"
            value={formData.certificateCode}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, certificateCode: e.target.value }));
              clearError('certificateCode');
            }}
            placeholder="Enter certificate code (GUID format)"
            error={errors.certificateCode}
          />

          <div className="mt-6">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              isLoading={isLoading}
            >
              Verify Certificate
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default VerifyCertificateForm;
